import { useRef, useCallback, useEffect, useState } from 'react'
import { chartingService } from '@/services/charting'
import usePositionStore from '@/store/positionStore'

function toCcxtSymbol(raw) {
  if (!raw) return raw
  if (raw.includes('/')) return raw
  let s = raw.replace(/\.P$/i, '').replace(/_/g, '-')
  if (s.includes('-')) {
    const [base, quote] = s.split('-')
    return `${base}/${quote}:${quote}`
  }
  const m = s.match(/^(.*?)(USDT|USDC|BTC|ETH|USD)$/i)
  if (m) return `${m[1]}/${m[2]}:${m[2]}`
  return raw
}

const DAILY_TFS = new Set(['1d', '1w', '1M'])

function formatDate(utcSeconds) {
  const d = new Date(utcSeconds * 1000)
  const y = d.getUTCFullYear()
  const m = String(d.getUTCMonth() + 1).padStart(2, '0')
  const day = String(d.getUTCDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

function normalizeTime(time, timeframe) {
  if (DAILY_TFS.has(timeframe) && typeof time === 'number') {
    return formatDate(time)
  }
  return time
}

function normalizeCandle(c, timeframe) {
  return { ...c, time: normalizeTime(c.time, timeframe) }
}

const TIMEFRAMES = [
  { label: '1m',  value: '1m',  seconds: 3   * 24 * 3600 },
  { label: '5m',  value: '5m',  seconds: 7   * 24 * 3600 },
  { label: '10m', value: '10m', seconds: 14  * 24 * 3600 },
  { label: '15m', value: '15m', seconds: 30  * 24 * 3600 },
  { label: '30m', value: '30m', seconds: 60  * 24 * 3600 },
  { label: '1h',  value: '1h',  seconds: 90  * 24 * 3600 },
  { label: '2h',  value: '2h',  seconds: 180 * 24 * 3600 },
  { label: '4h',  value: '4h',  seconds: 365 * 24 * 3600 },
  { label: '1D',  value: '1d',  seconds: 730 * 24 * 3600 },
  { label: '1S',  value: '1w',  seconds: 730 * 24 * 3600 },
  { label: '1M',  value: '1M',  seconds: 1825* 24 * 3600 },
]

function tfToSeconds(tf) {
  const m = { 'm': 60, 'h': 3600, 'd': 86400, 'w': 604800, 'M': 2592000 }
  const g = tf.match(/(\d+)([mhdwM])/)
  if (!g) return 3600
  return parseInt(g[1], 10) * m[g[2]]
}

export function useChartData(symbol, timeframe, source = 'bingx') {
  const currentTf = TIMEFRAMES.find(t => t.value === timeframe) || TIMEFRAMES[5]

  const candleDataRef   = useRef([])
  const isLoadingRef    = useRef(false)
  const errorRef        = useRef(null)
  const subscribersRef  = useRef(new Set())
  const requestIdRef    = useRef(0)
  const [tick, setTick] = useState(0)

  const notify = useCallback(() => {
    setTick(t => t + 1)
    subscribersRef.current.forEach(cb => cb())
  }, [])

  const notifySubscribers = useCallback(() => {
    subscribersRef.current.forEach(cb => cb())
  }, [])

  const load = useCallback(async () => {
    if (!symbol) return
    // Tag this request; if symbol/timeframe changes again before it resolves,
    // a newer load() bumps requestIdRef and this one's result is discarded
    // instead of overwriting fresher data with a stale/mismatched timeframe
    // (e.g. daily string times landing on top of intraday numeric times),
    // which is what used to crash the chart and required a page reload.
    const reqId = ++requestIdRef.current
    isLoadingRef.current = true
    errorRef.current = null
    notify()

    try {
      const to   = Math.floor(Date.now() / 1000)
      const from = to - (currentTf.seconds ?? 90 * 24 * 3600)
      const res  = await chartingService.getHistory({
        symbol:     toCcxtSymbol(symbol),
        resolution: timeframe,
        from_time:  from,
        to_time:    to,
        source:     source,
        _t:         Date.now(),
      })
      if (reqId !== requestIdRef.current) return // superseded by a newer request

      if (res.data?.s === 'ok' && res.data.t?.length > 0) {
        const candles = res.data.t
          .map((time, i) =>
            normalizeCandle({
              time,
              open:   res.data.o[i],
              high:   res.data.h[i],
              low:    res.data.l[i],
              close:  res.data.c[i],
              volume: res.data.v?.[i] ?? 0,
            }, timeframe)
          )
          // Descarta velas con time inválido o OHLC no numérico (gaps/errores del
          // proveedor de datos): lightweight-charts no valida estos valores al
          // recibirlos y sólo lanza "Value is null" de forma diferida (en el
          // siguiente frame de render), rompiendo el gráfico entero hasta recargar.
          .filter(c => c.time != null && c.time !== '' && ![c.time].some(Number.isNaN) && [c.open, c.high, c.low, c.close].every(Number.isFinite))
        // lightweight-charts requiere velas estrictamente ordenadas y sin tiempos
        // duplicados: con datos desordenados o duplicados (paginación del proveedor,
        // solapes al fusionar chunks, etc.) el "colorer" interno del candlestick
        // series (que decide si una barra es alcista/bajista) revienta con el mismo
        // "Value is null" diferido, sin relación con que open/high/low/close sean
        // válidos. Ver https://github.com/tradingview/lightweight-charts/issues/568
        candles.sort((a, b) => (a.time > b.time ? 1 : a.time < b.time ? -1 : 0))
        const deduped = []
        for (const c of candles) {
          if (deduped.length > 0 && deduped[deduped.length - 1].time === c.time) {
            deduped[deduped.length - 1] = c // se queda con la versión más reciente de ese tiempo
          } else {
            deduped.push(c)
          }
        }
        candleDataRef.current = deduped
        errorRef.current = null
      } else {
        candleDataRef.current = []
        errorRef.current = res.data?.errmsg || 'No data returned'
      }
    } catch (e) {
      if (reqId !== requestIdRef.current) return // superseded by a newer request
      candleDataRef.current = []
      errorRef.current = e.response?.data?.detail || e.message || 'Failed to load data'
    } finally {
      if (reqId === requestIdRef.current) {
        isLoadingRef.current = false
        // Usamos setTick en lugar de notify para que ChartRenderer haga setData
        // completo; evitamos que el subscriber haga update con datos potencialmente
        // desfasados contra la serie anterior.
        setTick(t => t + 1)
      }
    }
  }, [symbol, timeframe, source, currentTf.seconds, setTick])

  useEffect(() => { load() }, [load])

  useEffect(() => {
    const baseMs = {
      '1m': 10000, '5m': 30000, '10m': 30000, '15m': 30000,
      '30m': 60000, '1h': 60000, '2h': 120000, '4h': 120000,
      '1d': 300000, '1w': 600000, '1M': 1800000,
    }[timeframe] || 60000
    // Twelve Data free tier: 8 req/min (~7.5s) y 800 req/día.
    // Alargamos el polling para no saturar la API.
    const intervalMs = source === 'twelve' ? Math.max(baseMs, 60000) : baseMs
    const interval = setInterval(() => load(), intervalMs)
    return () => clearInterval(interval)
  }, [load, timeframe, source])

  // Actualizar la última vela en tiempo real con los precios del WebSocket.
  // Para timeframes diarios+ no aplicamos porque usan string 'YYYY-MM-DD'.
  useEffect(() => {
    if (!symbol || DAILY_TFS.has(timeframe)) return
    const targetSymbol = toCcxtSymbol(symbol)
    const tfSec = tfToSeconds(timeframe)
    return usePositionStore.subscribe((state) => {
      // No mutar velas mientras se carga un nuevo timeframe/símbolo: los datos
      // de candleDataRef pueden ser todavía del periodo anterior y el tick en
      // vivo podría mezclar tiempos/valores inconsistentes.
      if (isLoadingRef.current) return
      const price = state.prices[targetSymbol]
      // Number.isFinite (no == null): un precio NaN/no numérico del WS se colaba
      // en Math.max/Math.min y corrompía la última vela, provocando el mismo
      // "Value is null" diferido en el candlestick series.
      if (!Number.isFinite(price)) return
      const arr = candleDataRef.current
      if (arr.length === 0) return
      // Sanity-check de la última vela: si por alguna razón tiene un time
      // inválido, no intentar mutarla ni empujar una nueva encima.
      const last = arr[arr.length - 1]
      if (last.time == null || last.time === '' || Number.isNaN(last.time)) return
      const nowSec = Math.floor(Date.now() / 1000)
      const currentCandleTime = Math.floor(nowSec / tfSec) * tfSec
      if (currentCandleTime === last.time) {
        const high = Math.max(last.high, price)
        const low = Math.min(last.low, price)
        arr[arr.length - 1] = { ...last, close: price, high, low }
      } else if (currentCandleTime > last.time) {
        arr.push({ time: currentCandleTime, open: price, high: price, low: price, close: price, volume: 0 })
        if (arr.length > 5000) arr.shift()
      } else {
        return
      }
      notifySubscribers()
    })
  }, [symbol, timeframe, notifySubscribers])

  const updateLastCandle = useCallback((candle) => {
    const arr = candleDataRef.current
    if (arr.length === 0) return
    const norm = normalizeCandle(candle, timeframe)
    const last = arr[arr.length - 1]
    if (norm.time === last.time) {
      arr[arr.length - 1] = norm
    } else if (norm.time > last.time) {
      arr.push(norm)
      if (arr.length > 5000) arr.shift()
    }
    notify()
  }, [notify, timeframe])

  const subscribe = useCallback((cb) => {
    subscribersRef.current.add(cb)
    return () => subscribersRef.current.delete(cb)
  }, [])

  return {
    data: candleDataRef.current,
    dataRef: candleDataRef,
    isLoading: isLoadingRef.current,
    error: errorRef.current,
    reload: load,
    updateLastCandle,
    subscribe,
    tick,
  }
}
